import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  MenuItem,
} from "@mui/material";
import { ArrowLeft2, CloseCircle, DocumentUpload, Trash } from "iconsax-react";
import TemplateMessageDisplay from "../TemplateMessageDisplay";
import { useDispatch, useSelector } from "react-redux";
import { sendTemplateMessage, clearSendState } from "../../../../redux/Slice/Templates/templateSlice";
import { addMessage } from "../../../../redux/Slice/Inbox/InboxMessageSlice";
import { fetchCatalogs } from "../../../../redux/Slice/Catalog/catalogSlice";
import { getSocketId } from "../../../../utils/socketHandler";
import { getAuthToken } from "../../../../utils/getAuthToken";
import { isSubscriptionBlockedError, checkBeforeOutbound } from "../../../../utils/subscriptionError";
import toast from "react-hot-toast";
import { validateInstagramImageFile, validateInstagramMediaTemplateFile } from "../../../../utils/instagramMediaValidation";
import CatalogSend from "./CatalogSend";

const MAX_BUTTONS = 3;
const MAX_GENERIC_ELEMENTS = 10;
const MAX_LIST_ELEMENTS = 4; // Meta Messenger list: 2–4 elements

/** Native <select> mis-positions inside Dialog/scroll (xs/sm). MUI MenuItem + portal + z-index above modal. */
const TEMPLATE_TYPE_SELECT_PROPS = {
  MenuProps: {
    disablePortal: false,
    disableScrollLock: true,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    PaperProps: {
      sx: (theme) => ({
        maxHeight: 280,
        zIndex: theme.zIndex.modal + 100,
      }),
    },
  },
};

// Inline placeholder so preview never depends on external URLs (e.g. via.placeholder.com)
const MEDIA_PLACEHOLDER_DATA_URI = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect fill='%23f0f0f0' width='400' height='300'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16'%3EImage%3C/text%3E%3C/svg%3E";

/**
 * Build current payload for preview (and validation). Does not include empty optional fields.
 */
function buildPreviewPayload(templateType, formState) {
  const state = formState;
  if (templateType === "button") {
    const buttons = (state.buttons || [])
      .filter((b) => (b.title || "").trim())
      .filter((b) => (b.type || "postback") !== "phone_number")
      .map((b) => ({
        type: (b.type || "postback") === "phone_number" ? "postback" : (b.type || "postback"),
        title: (b.title || "").slice(0, 20),
        ...(b.type === "web_url" && b.url ? { url: b.url } : {}),
        ...((b.type === "postback" || b.type === "phone_number") && b.payload ? { payload: b.payload } : {}),
      }));
    return {
      template_type: "button",
      text: (state.text || "").slice(0, 640),
      buttons: buttons.length ? buttons : [{ type: "postback", title: "Button", payload: "p" }],
    };
  }
  if (templateType === "generic") {
    const elements = (state.elements || []).slice(0, MAX_GENERIC_ELEMENTS).filter((el) => (el.title || "").trim()).map((el) => ({
      title: (el.title || "").slice(0, 80),
      subtitle: (el.subtitle || "").slice(0, 80),
      image_url: el.image_url || undefined,
      ...(el.default_action_url && /^https?:\/\//i.test(String(el.default_action_url).trim()) ? { default_action: { type: "web_url", url: String(el.default_action_url).trim() } } : {}),
      buttons: (el.buttons || []).filter((b) => (b.title || "").trim()).filter((b) => (b.type || "web_url") !== "phone_number").slice(0, 3).map((b) => ({
        type: (b.type || "web_url") === "phone_number" ? "postback" : (b.type || "web_url"),
        title: (b.title || "").slice(0, 20),
        url: b.url || "#",
        payload: b.payload || "p",
      })),
    }));
    if (!elements.length) {
      return { template_type: "generic", elements: [{ title: "Card title", subtitle: "Subtitle", buttons: [] }] };
    }
    return { template_type: "generic", elements };
  }
  if (templateType === "media") {
    const hasMedia = state.media_url || state.media_attachment_id;
    // Preview needs a displayable URL: use selected/uploaded media URL when available, else safe inline placeholder
    const previewUrl = hasMedia && state.media_url ? state.media_url : MEDIA_PLACEHOLDER_DATA_URI;
    const el = hasMedia
      ? {
          media_type: state.media_type || "image",
          ...(state.media_attachment_id ? { attachment_id: state.media_attachment_id } : {}),
          url: previewUrl,
          image_url: previewUrl,
          buttons: (state.media_buttons || []).filter((b) => (b.title || "").trim()).slice(0, 3).map((b) => ({
            type: b.type || "web_url",
            title: (b.title || "").slice(0, 20),
            url: b.url || "#",
            payload: b.payload || "p",
          })),
        }
      : { media_type: "image", url: MEDIA_PLACEHOLDER_DATA_URI, image_url: MEDIA_PLACEHOLDER_DATA_URI, buttons: [] };
    return { template_type: "media", elements: [el] };
  }
  if (templateType === "product") {
    const products = state.catalogSelection?.products;
    const elements = Array.isArray(products) && products.length > 0 ? products.map((p) => ({ id: p.id })) : [];
    if (elements.length === 0 && (state.product_ids || "").trim()) {
      const ids = String(state.product_ids).split(",").map((id) => id.trim()).filter(Boolean);
      return { template_type: "product", elements: ids.map((id) => ({ id })) };
    }
    return { template_type: "product", elements: elements.length ? elements : [{ id: "catalog_product_id" }] };
  }
  if (templateType === "list") {
    const listElements = (state.list_elements || []).filter((el) => (el.title || "").trim()).slice(0, MAX_LIST_ELEMENTS);
    const payload = {
      template_type: "list",
      top_element_style: state.list_top_element_style || "compact",
      elements: listElements.map((el) => ({
        title: (el.title || "").slice(0, 80),
        ...(el.subtitle ? { subtitle: (el.subtitle || "").slice(0, 80) } : {}),
        ...(el.image_url && /^https?:\/\//i.test(String(el.image_url).trim()) ? { image_url: String(el.image_url).trim() } : {}),
      })),
    };
    const listBtn = state.list_button;
    if (listBtn && (listBtn.title || "").trim() && listBtn.url && /^https?:\/\//i.test(String(listBtn.url).trim())) {
      payload.buttons = [{ type: "web_url", title: (listBtn.title || "").trim().slice(0, 20), url: String(listBtn.url).trim() }];
    }
    return payload;
  }
  return { template_type: templateType };
}

/**
 * Build final payload for API (strict for Meta).
 */
function buildSendPayload(templateType, formState) {
  const state = formState;
  const isValidHttpUrl = (s) => /^https?:\/\//i.test((s || "").trim());
  if (templateType === "button") {
    const text = (state.text || "").trim().slice(0, 640);
    const buttons = (state.buttons || []).filter((b) => (b.title || "").trim()).slice(0, MAX_BUTTONS).map((b) => {
      const type = (b.type || "postback").toLowerCase();
      if (type === "phone_number") return null;
      const title = (b.title || "").trim().slice(0, 20);
      if (type === "web_url") {
        const url = (b.url || "").trim();
        if (!isValidHttpUrl(url)) return null;
        return { type: "web_url", url, title };
      }
      return { type: "postback", title, payload: (b.payload || "").trim() || title };
    }).filter(Boolean);
    return { template_type: "button", text: text || "Message", buttons: buttons.length ? buttons : [] };
  }
  if (templateType === "generic") {
    const elements = (state.elements || []).slice(0, MAX_GENERIC_ELEMENTS).filter((el) => (el.title || "").trim()).map((el) => {
      const card = {
        title: (el.title || "").trim().slice(0, 80),
        subtitle: (el.subtitle || "").trim().slice(0, 80),
        image_url: (el.image_url || "").trim() || undefined,
      };
      const defaultActionUrl = (el.default_action_url || "").trim();
      if (defaultActionUrl && isValidHttpUrl(defaultActionUrl)) card.default_action = { type: "web_url", url: defaultActionUrl };
      const btns = (el.buttons || []).filter((b) => (b.title || "").trim()).slice(0, 3).map((b) => {
        const t = (b.type || "web_url").toLowerCase();
        if (t === "phone_number") return null;
        const title = (b.title || "").trim().slice(0, 20);
        if (t === "web_url") {
          const url = (b.url || "").trim();
          if (!isValidHttpUrl(url)) return null;
          return { type: "web_url", title, url };
        }
        return { type: "postback", title, payload: (b.payload || "").trim() || title };
      }).filter(Boolean);
      if (btns.length) card.buttons = btns;
      return card;
    });
    return { template_type: "generic", elements: elements.length ? elements : [{ title: "Title", subtitle: "Subtitle" }] };
  }
  if (templateType === "media") {
    const media_url = (state.media_url || "").trim();
    const media_attachment_id = state.media_attachment_id;
    if (!media_url && !media_attachment_id) return null;
    const isValidHttpUrl = (s) => /^https?:\/\//i.test((s || "").trim());
    const el = {
      media_type: (state.media_type || "image").toLowerCase(),
    };
    if (media_attachment_id) {
      el.attachment_id = media_attachment_id;
      // Persist display URL for inbox/chat UI (Meta Send API uses attachment_id only; server strips url/image_url before Graph).
      if (media_url && isValidHttpUrl(media_url)) {
        el.image_url = media_url;
        el.url = media_url;
      }
    } else if (media_url) {
      el.url = media_url;
    }
    const btns = (state.media_buttons || []).filter((b) => (b.title || "").trim()).slice(0, 3).map((b) => {
      const t = (b.type || "web_url").toLowerCase();
      if (t === "phone_number") return null;
      const title = (b.title || "").trim().slice(0, 20);
      if (t === "web_url") {
        const url = (b.url || "").trim();
        if (!isValidHttpUrl(url)) return null;
        return { type: "web_url", title, url };
      }
      return { type: "postback", title, payload: (b.payload || "").trim() || title };
    }).filter(Boolean);
    if (btns.length) el.buttons = btns;
    return { template_type: "media", elements: [el] };
  }
  if (templateType === "product") {
    const products = state.catalogSelection?.products;
    if (Array.isArray(products) && products.length > 0) {
      return { template_type: "product", elements: products.map((p) => ({ id: p.id })) };
    }
    const productIds = (state.product_ids || "").split(",").map((id) => id.trim()).filter(Boolean);
    if (!productIds.length) return null;
    return { template_type: "product", elements: productIds.map((id) => ({ id })) };
  }
  if (templateType === "list") {
    const listElements = (state.list_elements || [])
      .filter((el) => (el.title || "").trim())
      .slice(0, MAX_LIST_ELEMENTS)
      .map((el) => ({
        title: (el.title || "").trim().slice(0, 80),
        ...(el.subtitle && (el.subtitle || "").trim() ? { subtitle: (el.subtitle || "").trim().slice(0, 80) } : {}),
        ...(el.image_url && isValidHttpUrl(String(el.image_url).trim()) ? { image_url: String(el.image_url).trim() } : {}),
      }));
    if (listElements.length < 2) return null; // Meta: min 2 elements
    const payload = {
      template_type: "list",
      top_element_style: (state.list_top_element_style || "compact").toLowerCase(),
      elements: listElements,
    };
    const listBtn = state.list_button;
    if (listBtn && (listBtn.title || "").trim() && listBtn.url && isValidHttpUrl(String(listBtn.url).trim())) {
      payload.buttons = [{ type: "web_url", title: (listBtn.title || "").trim().slice(0, 20), url: String(listBtn.url).trim() }];
    }
    return payload;
  }
  return null;
}

const INITIAL_FORM_STATE = {
    text: "",
    buttons: [{ type: "postback", title: "", payload: "" }],
    elements: [{ title: "", subtitle: "", image_url: "", default_action_url: "", buttons: [] }],
    media_type: "image",
    media_url: "",
    media_attachment_id: "",
  media_attachment_uploaded_at: "",
    media_buttons: [],
    product_ids: "",
    catalogSelection: null,
    list_elements: [{ title: "", subtitle: "", image_url: "" }, { title: "", subtitle: "", image_url: "" }],
    list_top_element_style: "compact",
    list_button: { title: "", url: "" },
};

const InstagramTemplateForm = ({ open, onClose, onBack, templateType, contactID, platform = "instagram" }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const fieldSize = isXsScreen ? "small" : "medium";
  const iconBtnSize = isXsScreen ? "small" : "medium";
  const { sendStatus } = useSelector((state) => state.template);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  /** In-dialog feedback only (above MUI modal); do not use react-hot-toast here — keeps IG/FB template UX isolated from global toasts. */
  const notify = (message, severity = "success") => {
    if (!message) return;
    setSnackbar({ open: true, message: String(message), severity });
  };
  const [formState, setFormState] = useState(INITIAL_FORM_STATE);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [mediaLibraryList, setMediaLibraryList] = useState([]);
  const [mediaLibraryLoading, setMediaLibraryLoading] = useState(false);
  const [removingLibraryId, setRemovingLibraryId] = useState(null);

  // Reset form when dialog opens so each open starts fresh; clear shared send slice so stale errors do not linger
  useEffect(() => {
    if (open) {
      setFormState(INITIAL_FORM_STATE);
      dispatch(clearSendState());
    }
  }, [open, dispatch]);

  const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

  const isValidHttpUrl = (s) => /^https?:\/\//i.test((s || "").trim());
  const previewPayload = useMemo(() => buildPreviewPayload(templateType, formState), [templateType, formState]);
  const canSend = useMemo(() => {
    const p = buildSendPayload(templateType, formState);
    if (!p) return false;
    if (templateType === "button") {
      const hasText = (p.text || "").length > 0;
      const buttons = formState.buttons || [];
      const allUrlButtonsValid = buttons
        .filter((b) => (b.type || "").toLowerCase() === "web_url")
        .every((b) => isValidHttpUrl(b.url));
      return hasText && allUrlButtonsValid;
    }
    if (templateType === "generic") {
      const elements = formState.elements || [];
      const allElementUrlButtonsValid = elements.every((el) =>
        (el.buttons || []).filter((b) => (b.type || "").toLowerCase() === "web_url").every((b) => isValidHttpUrl(b.url))
      );
      return p.elements?.length > 0 && allElementUrlButtonsValid;
    }
    if (templateType === "media") {
      const mediaButtons = formState.media_buttons || [];
      const allMediaUrlButtonsValid = mediaButtons
        .filter((b) => (b.type || "").toLowerCase() === "web_url")
        .every((b) => isValidHttpUrl(b.url));
      const hasMediaUploadedToMeta = (p.elements && p.elements[0] && p.elements[0].attachment_id);
      return hasMediaUploadedToMeta && allMediaUrlButtonsValid;
    }
    if (templateType === "product") return (p.elements && p.elements.length > 0);
    if (templateType === "list") {
      const listEls = (formState.list_elements || []).filter((el) => (el.title || "").trim());
      if (listEls.length < 2) return false;
      const listBtn = formState.list_button;
      if (listBtn && (listBtn.title || "").trim()) {
        if (!listBtn.url || !isValidHttpUrl(listBtn.url)) return false;
      }
      return true;
    }
    return true;
  }, [templateType, formState]);

  const selectedContactID = useSelector((state) => state?.inboxAll?.selectedContactID);
  const contactInfo = useSelector((state) => state?.inboxInfo?.contactInfo);
  const authUser = useSelector((state) => state?.auth?.user);

  const handleSend = async () => {
    const payload = buildSendPayload(templateType, formState);
    if (!payload || !contactID) return;
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, ['templateSends']);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    const originSocketId = getSocketId();
    try {
      await dispatch(
        sendTemplateMessage({
          platform: platform || "instagram",
          contactId: contactID,
          templatePayload: payload,
          tempId,
          originSocketId,
        })
      ).unwrap();
      // Instagram & Facebook: add sent template to main chat in real time (sender does not receive receive_message)
      if (platform === "facebook" || platform === "instagram") {
        const isForActiveChat = selectedContactID && contactID && String(selectedContactID) === String(contactID);
        const bodyStr = typeof payload === "string" ? payload : JSON.stringify(payload);
        const contactName = contactInfo && contactInfo._id && String(contactInfo._id) === String(contactID) ? (contactInfo.name || "Unknown") : "Unknown";
        const phoneNumber = contactInfo && contactInfo._id && String(contactInfo._id) === String(contactID) ? (contactInfo.phoneNumber || contactInfo.phoneNumberID || "") : "";
        const now = new Date().toISOString();
        dispatch(addMessage({
          contactID: contactID,
          contactId: contactID,
          isForActiveChat: !!isForActiveChat,
          contactName,
          phoneNumber,
          contactNumber: phoneNumber,
          chat: {
            id: tempId,
            tempId,
            type: "template",
            messageType: "template",
            body: bodyStr,
            message: bodyStr,
            senderid: authUser?._id || null,
            sendername: authUser?.name || "Unknown",
            timestamp: now,
            time: now,
            status: "sent",
            platform: platform || "instagram",
          },
        }));
      }
      notify("Template sent", "success");
      setTimeout(() => {
        onClose();
      }, 800);
    } catch (e) {
      const msg =
        (typeof e === "string" && e) ||
        e?.userFriendlyMessage ||
        e?.message ||
        "Failed to send";
      // Subscription and usage-limit errors are handled by the global axios interceptor
      // (networkErrorHandler.js) which already shows a top-right toast — skip component Snackbar.
      if (!isSubscriptionBlockedError(msg)) {
        notify(msg, "error");
      }
    }
  };

  const update = (key, value) => setFormState((s) => ({ ...s, [key]: value }));
  const updateButton = (index, field, value) => {
    setFormState((s) => {
      const buttons = [...(s.buttons || [])];
      while (buttons.length <= index) buttons.push({ type: "postback", title: "", payload: "" });
      buttons[index] = { ...buttons[index], [field]: value };
      return { ...s, buttons };
    });
  };
  const addButton = () => {
    const cur = formState.buttons || [];
    if (cur.length >= MAX_BUTTONS) return;
    setFormState((s) => ({ ...s, buttons: [...(s.buttons || []), { type: "web_url", title: "", url: "" }] }));
  };
  const removeButton = (index) => {
    setFormState((s) => {
      const buttons = [...(s.buttons || [])];
      if (buttons.length <= 1) return s;
      buttons.splice(index, 1);
      return { ...s, buttons };
    });
  };
  const updateElement = (index, field, value) => {
    setFormState((s) => {
      const elements = [...(s.elements || [])];
      while (elements.length <= index) elements.push({ title: "", subtitle: "", image_url: "", default_action_url: "", buttons: [] });
      elements[index] = { ...elements[index], [field]: value };
      return { ...s, elements };
    });
  };
  const updateElementButton = (elIndex, btnIndex, field, value) => {
    setFormState((s) => {
      const elements = [...(s.elements || [])];
      while (elements.length <= elIndex) elements.push({ title: "", subtitle: "", image_url: "", default_action_url: "", buttons: [] });
      const buttons = [...(elements[elIndex].buttons || [])];
      while (buttons.length <= btnIndex) buttons.push({ type: "web_url", title: "", url: "" });
      buttons[btnIndex] = { ...buttons[btnIndex], [field]: value };
      elements[elIndex] = { ...elements[elIndex], buttons };
      return { ...s, elements };
    });
  };
  const addElementButton = (elIndex) => {
    const elements = formState.elements || [];
    const el = elements[elIndex];
    const cur = (el && el.buttons) ? el.buttons.length : 0;
    if (cur >= 3) return;
    setFormState((s) => {
      const next = [...(s.elements || [])];
      while (next.length <= elIndex) next.push({ title: "", subtitle: "", image_url: "", buttons: [] });
      next[elIndex] = { ...next[elIndex], buttons: [...(next[elIndex].buttons || []), { type: "web_url", title: "", url: "" }] };
      return { ...s, elements: next };
    });
  };
  const addElement = () => {
    if ((formState.elements || []).length >= MAX_GENERIC_ELEMENTS) return;
    setFormState((s) => ({ ...s, elements: [...(s.elements || []), { title: "", subtitle: "", image_url: "", default_action_url: "", buttons: [] }] }));
  };
  const removeElement = (index) => {
    setFormState((s) => {
      const elements = [...(s.elements || [])];
      if (elements.length <= 1) return s;
      elements.splice(index, 1);
      return { ...s, elements };
    });
  };
  const removeGenericCardButton = (elIndex, btnIndex) => {
    setFormState((s) => {
      const elements = [...(s.elements || [])];
      if (!elements[elIndex]) return s;
      const buttons = [...(elements[elIndex].buttons || [])];
      if (btnIndex < 0 || btnIndex >= buttons.length) return s;
      buttons.splice(btnIndex, 1);
      elements[elIndex] = { ...elements[elIndex], buttons };
      return { ...s, elements };
    });
  };
  const removeMediaButton = (index) => {
    setFormState((s) => {
      const buttons = [...(s.media_buttons || [])];
      if (index < 0 || index >= buttons.length) return s;
      buttons.splice(index, 1);
      return { ...s, media_buttons: buttons };
    });
  };
  const updateMediaButton = (index, field, value) => {
    setFormState((s) => {
      const buttons = [...(s.media_buttons || [])];
      while (buttons.length <= index) buttons.push({ type: "web_url", title: "", url: "", payload: "" });
      buttons[index] = { ...buttons[index], [field]: value };
      return { ...s, media_buttons: buttons };
    });
  };
  const addMediaButton = () => {
    const cur = formState.media_buttons || [];
    if (cur.length >= MAX_BUTTONS) return;
    setFormState((s) => ({ ...s, media_buttons: [...(s.media_buttons || []), { type: "web_url", title: "", url: "", payload: "" }] }));
  };

  const updateListElement = (index, field, value) => {
    setFormState((s) => {
      const list = [...(s.list_elements || [])];
      while (list.length <= index) list.push({ title: "", subtitle: "", image_url: "" });
      list[index] = { ...list[index], [field]: value };
      return { ...s, list_elements: list };
    });
  };
  const addListElement = () => {
    if ((formState.list_elements || []).length >= MAX_LIST_ELEMENTS) return;
    setFormState((s) => ({ ...s, list_elements: [...(s.list_elements || []), { title: "", subtitle: "", image_url: "" }] }));
  };
  const removeListElement = (index) => {
    const list = (formState.list_elements || []).filter((_, i) => i !== index);
    if (list.length < 2) return;
    setFormState((s) => ({ ...s, list_elements: list }));
  };
  const updateListButton = (field, value) => {
    setFormState((s) => ({ ...s, list_button: { ...(s.list_button || { title: "", url: "" }), [field]: value } }));
  };

  const handleInstagramImageUpload = async (e, setUrl) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateInstagramImageFile(file);
    if (!validation.valid) {
      notify(validation.error, "error");
      return;
    }
    const token = await getAuthToken();
    if (!token) {
      notify("Not authenticated", "error");
      return;
    }
    setUploadingKey("image");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("platform", platform || "instagram");
    try {
      const res = await fetch(`${SERVER_DOMAIN}/api/templates/sendupload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      const url = data?.url ?? data?.data;
      if (!url) {
        notify(data?.userFriendlyMessage || data?.error || "Upload failed", "error");
        return;
      }
      setUrl(url);
      notify("Image uploaded", "success");
    } catch (err) {
      notify(err?.message || "Upload failed", "error");
    } finally {
      setUploadingKey(null);
    }
  };

  const handleInstagramMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateInstagramMediaTemplateFile(file);
    if (!validation.valid) {
      notify(validation.error, "error");
      return;
    }
    const token = await getAuthToken();
    if (!token) {
      notify("Not authenticated", "error");
      return;
    }
    setUploadingKey("media");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("platform", platform || "instagram");
    try {
      const res = await fetch(`${SERVER_DOMAIN}/api/templates/sendupload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      const url = data?.url ?? data?.data;
      if (!url) {
        notify(data?.userFriendlyMessage || data?.error || "Upload failed", "error");
        return;
      }
      setFormState((s) => ({ ...s, media_url: url }));
      const mediaType = formState.media_type === "video" ? "video" : "image";
      const attachmentEndpoint = platform === "facebook"
        ? `${SERVER_DOMAIN}/api/templates/facebook-attachment`
        : `${SERVER_DOMAIN}/api/templates/instagram-attachment`;
      const attachRes = await fetch(attachmentEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url, mediaType, displayName: file.name || null }),
      });
      const attachData = await attachRes.json().catch(() => ({}));
      if (attachData.attachment_id) {
        setFormState((s) => ({
          ...s,
          media_attachment_id: attachData.attachment_id,
          ...(attachData.uploaded_at ? { media_attachment_uploaded_at: attachData.uploaded_at } : {}),
        }));
        notify("Media uploaded", "success");
        // Refresh media library so new upload appears in list
        try {
          const libRes = await fetch(
            `${SERVER_DOMAIN}/api/templates/media-library?platform=${platform}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const libData = await libRes.json().catch(() => ({}));
          if (libData.items) setMediaLibraryList(libData.items);
        } catch (e) { /* ignore */ }
      } else if (attachData.error) {
        notify(attachData.userFriendlyMessage || attachData.error, "error");
      } else {
        notify("File saved; upload to Meta failed. Try again.", "warning");
      }
    } catch (err) {
      notify(err?.message || "Upload failed", "error");
    } finally {
      setUploadingKey(null);
    }
  };

  useEffect(() => {
    if (open && templateType === "product") {
      // Per Meta: fetch catalog for current channel (Instagram or Facebook) so product IDs match the Page/business
      const catalogPlatform = platform === "facebook" ? "facebook" : "instagram";
      dispatch(fetchCatalogs(catalogPlatform));
    }
  }, [open, templateType, platform, dispatch]);

  // Fetch media library when opening media template (Instagram or Facebook)
  useEffect(() => {
    if (!open || templateType !== "media" || (platform !== "instagram" && platform !== "facebook")) {
      return;
    }
    let cancelled = false;
    const fetchLibrary = async () => {
      setMediaLibraryLoading(true);
      try {
        const token = await getAuthToken();
        if (!token) return;
        const res = await fetch(
          `${SERVER_DOMAIN}/api/templates/media-library?platform=${platform}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json().catch(() => ({}));
        if (!cancelled && data.items) setMediaLibraryList(data.items);
      } catch (e) {
        if (!cancelled) setMediaLibraryList([]);
      } finally {
        if (!cancelled) setMediaLibraryLoading(false);
      }
    };
    fetchLibrary();
    return () => { cancelled = true; };
  }, [open, templateType, platform, SERVER_DOMAIN]);

  if (!templateType) return null;

  const renderForm = () => {
    if (templateType === "button") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, sm: 2.5 } }}>
          <TextField size={fieldSize} label="Text (max 640 characters, Meta)" multiline minRows={isXsScreen ? 2 : 3} value={formState.text} onChange={(e) => update("text", e.target.value.slice(0, 640))} fullWidth inputProps={{ maxLength: 640, style: { fontSize: "1rem" } }} helperText="Button template text: up to 640 characters" />
          {(formState.buttons || []).map((b, i) => (
            <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, sm: 1.5 }, p: { xs: 1, sm: 1.5 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 32 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Button {i + 1}
                </Typography>
                {(formState.buttons || []).length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeButton(i)}
                    aria-label={`Remove button ${i + 1}`}
                    sx={{ color: "text.secondary" }}
                  >
                    <Trash size="16" variant="Linear" color="currentColor" />
                  </IconButton>
                )}
              </Box>
              <TextField size={fieldSize} label="Button title" value={b.title} onChange={(e) => updateButton(i, "title", e.target.value)} inputProps={{ style: { fontSize: "1rem" } }} />
              <TextField select SelectProps={TEMPLATE_TYPE_SELECT_PROPS} size={fieldSize} label="Type" value={b.type === "phone_number" ? "postback" : (b.type || "postback")} onChange={(e) => updateButton(i, "type", e.target.value)}>
                <MenuItem value="postback">Postback</MenuItem>
                <MenuItem value="web_url">URL</MenuItem>
              </TextField>
              {(b.type === "web_url" && (
                <TextField
                  size={fieldSize}
                  label="URL"
                  value={b.url}
                  onChange={(e) => updateButton(i, "url", e.target.value)}
                  helperText="Must start with http:// or https://"
                  error={!!(b.url && !isValidHttpUrl(b.url))}
                  inputProps={{ style: { fontSize: "1rem" } }}
                />
              )) ||
                (b.type !== "web_url" && (
                  <TextField
                    size={fieldSize}
                    label="Payload"
                    value={b.payload == null ? "" : String(b.payload)}
                    onChange={(e) => updateButton(i, "payload", e.target.value)}
                    inputProps={{ style: { fontSize: "1rem" } }}
                    InputLabelProps={{ shrink: true }}
                  />
                ))}
            </Box>
          ))}
          {(formState.buttons || []).length < MAX_BUTTONS && <Button size={fieldSize} onClick={addButton}>Add button</Button>}
        </Box>
      );
    }
    if (templateType === "generic") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, sm: 2.5 } }}>
          {(formState.elements || []).map((el, i) => (
            <Box key={i} sx={{ p: { xs: 1, sm: 2 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: { xs: 1, sm: 1.5 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: "0.9rem", sm: "1rem" } }}>Card {i + 1}</Typography>
                {(formState.elements || []).length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeElement(i)}
                    aria-label={`Remove card ${i + 1}`}
                    sx={{ color: "text.secondary" }}
                  >
                    <Trash size="16" variant="Linear" color="currentColor" />
                  </IconButton>
                )}
              </Box>
              <TextField size={fieldSize} fullWidth label="Title" value={el.title} onChange={(e) => updateElement(i, "title", e.target.value)} sx={{ mb: { xs: 1, sm: 1.5 } }} inputProps={{ maxLength: 80, style: { fontSize: "1rem" } }} />
              <TextField size={fieldSize} fullWidth label="Subtitle" value={el.subtitle} onChange={(e) => updateElement(i, "subtitle", e.target.value)} sx={{ mb: { xs: 1, sm: 1.5 } }} inputProps={{ maxLength: 80, style: { fontSize: "1rem" } }} />
              <TextField size={fieldSize} fullWidth label="Default action URL (optional)" value={el.default_action_url || ""} onChange={(e) => updateElement(i, "default_action_url", e.target.value)} placeholder="Opens when card is tapped" sx={{ mb: { xs: 1, sm: 1.5 } }} inputProps={{ style: { fontSize: "1rem" } }} helperText="Meta: optional URL when user taps the card" />
              <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Image (upload only)</Typography>
                <Button component="label" variant="outlined" size="small" startIcon={<DocumentUpload size="18" variant="Linear" />} disabled={!!uploadingKey}>
                  {uploadingKey === "image" ? "Uploading…" : "Upload image"}
                  <input type="file" hidden accept="image/*" onChange={(e) => handleInstagramImageUpload(e, (url) => updateElement(i, "image_url", url))} />
                </Button>
                {el.image_url && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>Uploaded</Typography>}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 1 }}>Buttons (up to 3 per card, Meta)</Typography>
              {(el.buttons || []).map((b, j) => (
                <Box key={j} sx={{ display: "flex", flexDirection: "column", gap: 1, pl: { xs: 1, sm: 1.5 }, mb: { xs: 1, sm: 1.5 }, borderLeft: "2px solid", borderColor: "divider" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 32 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Button {j + 1}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeGenericCardButton(i, j)}
                      aria-label={`Remove button ${j + 1} from card ${i + 1}`}
                      sx={{ color: "text.secondary" }}
                    >
                      <Trash size="16" variant="Linear" color="currentColor" />
                    </IconButton>
                  </Box>
                  <TextField size={fieldSize} label="Button title" value={b.title} onChange={(e) => updateElementButton(i, j, "title", e.target.value)} inputProps={{ maxLength: 20, style: { fontSize: "1rem" } }} />
                  <TextField select SelectProps={TEMPLATE_TYPE_SELECT_PROPS} size={fieldSize} label="Type" value={b.type === "phone_number" ? "postback" : (b.type || "web_url")} onChange={(e) => updateElementButton(i, j, "type", e.target.value)}>
                    <MenuItem value="web_url">URL</MenuItem>
                    <MenuItem value="postback">Postback</MenuItem>
                  </TextField>
                  {(b.type === "web_url" && (
                    <TextField size={fieldSize} label="URL" value={b.url == null ? "" : String(b.url)} onChange={(e) => updateElementButton(i, j, "url", e.target.value)} helperText="Must start with http:// or https://" error={!!(b.url && !isValidHttpUrl(b.url))} inputProps={{ style: { fontSize: "1rem" } }} />
                  )) ||
                    (b.type !== "web_url" && (
                      <TextField
                        size={fieldSize}
                        label="Payload"
                        value={b.payload == null ? "" : String(b.payload)}
                        onChange={(e) => updateElementButton(i, j, "payload", e.target.value)}
                        inputProps={{ style: { fontSize: "1rem" } }}
                        InputLabelProps={{ shrink: true }}
                      />
                    ))}
                </Box>
              ))}
              {(el.buttons || []).length < 3 && <Button size={fieldSize} onClick={() => addElementButton(i)} sx={{ mt: 0.5 }}>Add button to card</Button>}
            </Box>
          ))}
          {(formState.elements || []).length < MAX_GENERIC_ELEMENTS && <Button size={fieldSize} onClick={addElement}>Add card</Button>}
        </Box>
      );
    }
    if (templateType === "media") {
      const getRemainingDays = (uploadedAt) => {
        const up = new Date(uploadedAt).getTime();
        const expiresAt = up + 90 * 24 * 60 * 60 * 1000;
        const now = Date.now();
        if (now > expiresAt) return { expired: true, text: "Expired" };
        const days = Math.ceil((expiresAt - now) / (24 * 60 * 60 * 1000));
        return { expired: false, days, text: days <= 7 ? `Expires in ${days} day${days !== 1 ? "s" : ""}` : `Expires in ${days} days` };
      };
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, sm: 2.5 } }}>
          <TextField select SelectProps={TEMPLATE_TYPE_SELECT_PROPS} size={fieldSize} label="Media type" value={formState.media_type} onChange={(e) => update("media_type", e.target.value)}>
            <MenuItem value="image">Image</MenuItem>
            <MenuItem value="video">Video</MenuItem>
          </TextField>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Choose from library or upload new</Typography>
          {mediaLibraryLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={20} /><Typography variant="body2" color="text.secondary">Loading library…</Typography></Box>
          ) : mediaLibraryList.length > 0 ? (
            <Box sx={{ maxHeight: { xs: 180, sm: 220 }, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: { xs: 0.75, sm: 1 } }}>
              {mediaLibraryList.map((item) => {
                const { expired, text } = getRemainingDays(item.uploaded_at);
                const label = item.displayName || (item.mediaType === "video" ? "Video" : "Image");
                const isRemoving = removingLibraryId === item._id;
                const isSelected = !expired && formState.media_attachment_id === item.attachment_id && (formState.media_url || "") === (item.url || "");
                return (
                  <Box
                    key={item._id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 0.5,
                      py: 0.75,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: isSelected ? "primary.light" : expired ? "action.hover" : "transparent",
                      border: isSelected ? "2px solid" : "none",
                      borderColor: "primary.main",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap title={item.url}>{label}</Typography>
                      <Typography variant="caption" color={expired ? "error.main" : "text.secondary"}>{text}</Typography>
                    </Box>
                    <Button
                      size="small"
                      variant={isSelected ? "contained" : "outlined"}
                      disabled={expired}
                      onClick={() => setFormState((s) => ({ ...s, media_attachment_id: item.attachment_id, media_attachment_uploaded_at: item.uploaded_at || "", media_url: item.url || s.media_url }))}
                    >
                      {expired ? "Expired" : isSelected ? "Selected" : "Select"}
                    </Button>
                    <IconButton
                      size="small"
                      aria-label="Remove from library"
                      disabled={isRemoving}
                      onClick={async () => {
                        setRemovingLibraryId(item._id);
                        try {
                          const token = await getAuthToken();
                          if (!token) { notify("Not authenticated", "error"); return; }
                          const res = await fetch(`${SERVER_DOMAIN}/api/templates/media-library/${item._id}`, {
                            method: "DELETE",
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          const data = await res.json().catch(() => ({}));
                          if (res.ok && data.success) {
                            setMediaLibraryList((prev) => prev.filter((x) => x._id !== item._id));
                            notify("Removed from library", "success");
                          } else {
                            notify(data?.userFriendlyMessage || data?.error || "Failed to remove", "error");
                          }
                        } catch (e) {
                          notify(e?.message || "Failed to remove from library", "error");
                        } finally {
                          setRemovingLibraryId(null);
                        }
                      }}
                      sx={{ color: "text.secondary" }}
                    >
                      <Trash size="16" variant="Linear" color="currentColor" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">No uploads in library yet. Upload a file below to add it.</Typography>
          )}
          {formState.media_attachment_id ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="success.main" sx={{ display: "block" }}>
                Selected – will send as media template
                {formState.media_attachment_uploaded_at && (() => {
                  const uploadedAt = new Date(formState.media_attachment_uploaded_at).getTime();
                  const expiresAt = uploadedAt + 90 * 24 * 60 * 60 * 1000;
                  const now = Date.now();
                  if (now > expiresAt) return " • Expired – please re-upload";
                  const daysLeft = Math.ceil((expiresAt - now) / (24 * 60 * 60 * 1000));
                  if (daysLeft <= 7) return ` • Expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}`;
                  return ` • Valid for 90 days (expires ${new Date(expiresAt).toLocaleDateString()})`;
                })()}
              </Typography>
              <Button size="small" color="secondary" onClick={() => setFormState((s) => ({ ...s, media_attachment_id: "", media_attachment_uploaded_at: "", media_url: "" }))} sx={{ mt: 0.5 }}>
                Clear selection
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">Or upload new image/video:</Typography>
          <Button component="label" startIcon={<DocumentUpload size="18" variant="Linear" />} variant="outlined" size={fieldSize} disabled={!!uploadingKey}>
            {uploadingKey === "media" ? "Uploading…" : "Upload file"}
            <input type="file" hidden accept="image/*,video/*" onChange={handleInstagramMediaUpload} />
          </Button>
            </>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.5 }}>Buttons (optional, up to 3)</Typography>
          {(formState.media_buttons || []).map((b, i) => (
            <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: 1, pl: { xs: 1, sm: 1.5 }, py: 1, borderLeft: "2px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 32 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Button {i + 1}</Typography>
                <IconButton
                  size="small"
                  onClick={() => removeMediaButton(i)}
                  aria-label={`Remove media button ${i + 1}`}
                  sx={{ color: "text.secondary" }}
                >
                  <Trash size="16" variant="Linear" color="currentColor" />
                </IconButton>
              </Box>
              <TextField size={fieldSize} label="Button title" value={b.title} onChange={(e) => updateMediaButton(i, "title", e.target.value)} inputProps={{ maxLength: 20, style: { fontSize: "1rem" } }} />
              <TextField select SelectProps={TEMPLATE_TYPE_SELECT_PROPS} size={fieldSize} label="Type" value={(b.type || "web_url") === "phone_number" ? "web_url" : (b.type || "web_url")} onChange={(e) => updateMediaButton(i, "type", e.target.value)}>
                <MenuItem value="web_url">URL</MenuItem>
                <MenuItem value="postback">Postback</MenuItem>
              </TextField>
              {(b.type || "web_url") === "web_url" ? (
                <TextField size={fieldSize} label="URL" value={b.url == null ? "" : String(b.url)} onChange={(e) => updateMediaButton(i, "url", e.target.value)} helperText="Must start with http:// or https://" error={!!(b.url && !isValidHttpUrl(b.url))} inputProps={{ style: { fontSize: "1rem" } }} />
              ) : (
                <TextField
                  size={fieldSize}
                  label="Payload"
                  value={b.payload == null ? "" : String(b.payload)}
                  onChange={(e) => updateMediaButton(i, "payload", e.target.value)}
                  inputProps={{ style: { fontSize: "1rem" } }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Box>
          ))}
          {(formState.media_buttons || []).length < MAX_BUTTONS && (
            <Button size={fieldSize} onClick={addMediaButton} sx={{ alignSelf: "flex-start" }}>Add button</Button>
          )}
        </Box>
      );
    }
    if (templateType === "product") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, sm: 2.5 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Select catalog and products (same as workflow):</Typography>
          <CatalogSend
            platform={platform === "facebook" ? "facebook" : "instagram"}
            onCatalogSelect={(val) => setFormState((s) => ({ ...s, catalogSelection: val }))}
            selectedCatalog={formState.catalogSelection}
            compactLayout={isXsScreen}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Or enter product IDs manually:</Typography>
          <TextField size={fieldSize} label="Product IDs" value={formState.product_ids} onChange={(e) => update("product_ids", e.target.value)} placeholder="e.g. 123, 456" fullWidth inputProps={{ style: { fontSize: "1rem" } }} />
        </Box>
      );
    }
    if (templateType === "list") {
      const listEls = formState.list_elements || [];
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, sm: 2.5 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>List style (Messenger: first row)</Typography>
          <TextField select SelectProps={TEMPLATE_TYPE_SELECT_PROPS} size={fieldSize} label="Top element style" value={formState.list_top_element_style || "compact"} onChange={(e) => update("list_top_element_style", e.target.value)}>
            <MenuItem value="compact">Compact</MenuItem>
            <MenuItem value="large">Large</MenuItem>
          </TextField>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>List items (2–4 required, Meta)</Typography>
          {listEls.map((el, i) => (
            <Box key={i} sx={{ p: { xs: 1, sm: 1.5 }, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2">Item {i + 1}</Typography>
                {listEls.length > 2 && (
                  <Button size="small" color="error" onClick={() => removeListElement(i)}>Remove</Button>
                )}
              </Box>
              <TextField size={fieldSize} fullWidth label="Title (required)" value={el.title} onChange={(e) => updateListElement(i, "title", e.target.value)} sx={{ mb: 1 }} inputProps={{ maxLength: 80, style: { fontSize: "1rem" } }} />
              <TextField size={fieldSize} fullWidth label="Subtitle (optional)" value={el.subtitle || ""} onChange={(e) => updateListElement(i, "subtitle", e.target.value)} sx={{ mb: 1 }} inputProps={{ maxLength: 80, style: { fontSize: "1rem" } }} />
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Image (optional)</Typography>
                <Button component="label" variant="outlined" size="small" startIcon={<DocumentUpload size="18" variant="Linear" />} disabled={!!uploadingKey}>
                  {uploadingKey === "list-img" ? "Uploading…" : "Upload image"}
                  <input type="file" hidden accept="image/*" onChange={(e) => handleInstagramImageUpload(e, (url) => updateListElement(i, "image_url", url))} />
                </Button>
                {el.image_url && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>Uploaded</Typography>}
              </Box>
            </Box>
          ))}
          {listEls.length < MAX_LIST_ELEMENTS && (
            <Button size={fieldSize} onClick={addListElement}>Add list item</Button>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>Bottom button (optional, max 1)</Typography>
          <TextField size={fieldSize} fullWidth label="Button title" value={(formState.list_button || {}).title || ""} onChange={(e) => updateListButton("title", e.target.value)} sx={{ mb: 1 }} inputProps={{ maxLength: 20, style: { fontSize: "1rem" } }} placeholder="e.g. View more" />
          <TextField size={fieldSize} fullWidth label="Button URL" value={(formState.list_button || {}).url || ""} onChange={(e) => updateListButton("url", e.target.value)} helperText="Must start with http:// or https://" error={!!((formState.list_button || {}).url && !isValidHttpUrl((formState.list_button || {}).url))} inputProps={{ style: { fontSize: "1rem" } }} />
        </Box>
      );
    }
    return null;
  };

  const showPreview = ["button", "generic", "media", "list"].includes(templateType);
  const previewPayloadForDisplay = templateType === "product" ? null : previewPayload;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableScrollLock
        PaperProps={{
          sx: {
            minHeight: { xs: "min(72vh, 480px)", sm: 520 },
            maxHeight: { xs: "92vh", sm: "85vh" },
            m: { xs: 1, sm: 2 },
            width: { xs: "calc(100% - 16px)", sm: "auto" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: { xs: 2, sm: 3 },
          },
        }}
        sx={{
          "& .MuiButton-root": { borderRadius: "8px" },
          // This theme's base is 12px, so body2/caption render at ~10px or
          // smaller by default — the illegibility bug already documented in
          // ui-gotchas.md, and exactly what all the field labels/helper text
          // in this form were hitting (Rahim, 2026-08-24, screenshot: "the
          // font size... isnt even visible"). One rule here catches every
          // instance in the form instead of editing ~20 individual
          // Typography elements one at a time.
          "& .MuiTypography-body2": { fontSize: "0.8125rem" },
          "& .MuiTypography-caption": { fontSize: "0.75rem" },
        }}
      >
        <DialogContent
          sx={{
            p: { xs: 1.5, sm: 3 },
            pt: { xs: 1.25, sm: 2 },
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            "&.MuiDialogContent-root": { pt: { xs: 1.25, sm: 2 } },
          }}
        >
          {/* Was h5, responsively bumping to 1.25rem — flattened to the
              Section Title token (1rem/600) matching every other dialog
              header this session. Icons swapped to Iconsax. */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 1, sm: 2 }} gap={1} flexShrink={0}>
            <Box display="flex" alignItems="center" gap={0.5} flex={1} minWidth={0}>
              {typeof onBack === "function" && (
                <IconButton size={iconBtnSize} onClick={onBack} aria-label="Back to template types">
                  <ArrowLeft2 size={isXsScreen ? "18" : "20"} color={theme.palette.text.primary} variant="Linear" />
                </IconButton>
              )}
              <Typography sx={{ fontSize: "1rem", fontWeight: 600, flex: 1, color: "text.primary" }}>
                {platform === "facebook" ? "Facebook" : "Instagram"}: {templateType.charAt(0).toUpperCase() + templateType.slice(1)} template
              </Typography>
            </Box>
            <IconButton size={iconBtnSize} onClick={onClose} aria-label="Close dialog">
              <CloseCircle size={isXsScreen ? "18" : "20"} color={theme.palette.text.primary} variant="Linear" />
            </IconButton>
          </Box>
          {/* Negative mx to cancel out DialogContent's own horizontal padding
              — otherwise the divider sits inset within the padded content
              area instead of spanning the dialog's full width (Rahim,
              2026-08-24: "for the line under the title use full line"). */}
          <Divider sx={{ mb: { xs: 1, sm: 2 }, mx: { xs: -1.5, sm: -3 }, flexShrink: 0 }} />
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={{ xs: 1.5, sm: 3 }}
            flexWrap="nowrap"
            alignItems="stretch"
            sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
          >
            <Box
              flex={1}
              minWidth={{ xs: 0, sm: 320 }}
              minHeight={0}
              display="flex"
              flexDirection="column"
            >
              <Typography sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, flexShrink: 0, fontSize: "0.9375rem", color: "text.secondary" }}>
                Fields
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: { xs: 0, sm: 0.5 }, WebkitOverflowScrolling: "touch" }}>
                {renderForm()}
              </Box>
              <Box sx={{ mt: { xs: 1.25, sm: 2 }, pt: { xs: 1, sm: 1.5 }, flexShrink: 0, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                <Button variant="contained" size={fieldSize} onClick={handleSend} disabled={!canSend || sendStatus === "loading"} sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" }, py: { xs: 0.5, sm: 0.75 } }}>
                  {sendStatus === "loading" ? <CircularProgress size={isXsScreen ? 18 : 22} /> : "Send"}
                </Button>
              </Box>
            </Box>
            <Box
              flex={{ xs: "none", sm: "1 1 0" }}
              width={{ xs: "100%", sm: "auto" }}
              minWidth={{ xs: 0, sm: 280 }}
              minHeight={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: { xs: 1.5, sm: 2 },
                p: { xs: 1.25, sm: 2.5 },
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? theme.palette.background.paper
                    : theme.palette.background.default,
                overflow: "visible",
              }}
            >
              <Typography sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, fontSize: "0.9375rem", color: "text.secondary" }}>Preview</Typography>
              {showPreview && previewPayloadForDisplay && (
                <TemplateMessageDisplay
                  templateBody={JSON.stringify(previewPayloadForDisplay)}
                  fromMe
                  platform={platform === "facebook" ? "facebook" : "instagram"}
                />
              )}
              {templateType === "product" && (
                <Typography variant="body2" color="text.secondary">Product template (catalog). IDs: {formState.product_ids || "—"}</Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <Snackbar
          open={snackbar.open}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={
            snackbar.severity === "error" ? 8000 : snackbar.severity === "warning" ? 5500 : 4000
          }
          onClose={(_, reason) => {
            if (reason === "clickaway") return;
            setSnackbar((s) => ({ ...s, open: false }));
          }}
          sx={(theme) => ({
            zIndex: theme.zIndex.modal + 1400,
          })}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={["success", "error", "warning", "info"].includes(snackbar.severity) ? snackbar.severity : "info"}
            variant="filled"
            sx={{
              minWidth: { xs: "min(calc(100vw - 48px), 360px)", sm: 400 },
              maxWidth: 560,
              alignItems: "center",
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Dialog>
    </>
  );
};

export default InstagramTemplateForm;
